// El check-in de energía: cómo viene hoy el alumno, y qué se le deja de exigir.
//
// LA REGLA QUE MANDA SOBRE TODAS LAS DEMÁS: **baja lo que se EXIGE, nunca lo que se PUEDE
// hacer.** Un alumno que dice que viene con poca batería no se queda sin poder avanzar si
// luego se anima; lo que baja es lo que la plataforma le pide, le recuerda y le mete por los
// ojos. Al revés sería castigarle por haber sido honesto, y a la segunda vez ya no lo es.
//
// DÓNDE VIVE EL DATO, que es la otra decisión y es de privacidad: **en `sessionStorage`, y en
// ningún sitio más.** No sale del navegador, no llega al servidor y muere al cerrar la
// pestaña. Un registro histórico de «vengo con la batería baja» de una menor es un dato de
// salud mental sin llamarlo así, y no conviene tenerlo ni aunque nadie lo mire: lo que no se
// guarda no se filtra, no hay que borrarlo y no se lo puede pedir nadie.
//
// Y POR ESO LA PREGUNTA NO ES CLÍNICA. No se pregunta cómo está ni cómo se siente: se
// pregunta con cuánta batería viene, que es lo que hace falta saber para ajustar lo que se le
// pide. La misma regla que en `preferencias.mjs`: se describe por lo que hace, no por a quién
// va dirigido.
//
// Vive aparte y sin React porque son datos y reglas, y las dos se comprueban.

export const CLAVE = "alpha_checkin";

export const NIVELES = [
  {
    valor: "baja", titulo: "Poca", dice: "Hoy vengo justo.",
    // Lo que se le deja de pedir. Nada de esto le impide hacer más si le apetece.
    baja: { objetivo: 1, insiste: false, celebra: true },
  },
  {
    valor: "normal", titulo: "Normal", dice: "Como cualquier día.",
    baja: { objetivo: 2, insiste: true, celebra: true },
  },
  {
    valor: "alta", titulo: "Mucha", dice: "Hoy me pongo.",
    baja: { objetivo: 3, insiste: true, celebra: true },
  },
];

export const POR_DEFECTO = "normal";
export const POR_VALOR = Object.fromEntries(NIVELES.map((n) => [n.valor, n]));

/** Lo que hoy se le exige a quien ha contestado eso. Un valor desconocido no rompe nada. */
export function loQueSePide(valor) {
  return (POR_VALOR[valor] || POR_VALOR[POR_DEFECTO]).baja;
}

/** El día de hoy en el huso del navegador, para no arrastrar la respuesta de ayer. */
export function hoy(fecha = new Date()) {
  return [
    fecha.getFullYear(),
    String(fecha.getMonth() + 1).padStart(2, "0"),
    String(fecha.getDate()).padStart(2, "0"),
  ].join("-");
}

/**
 * Lo contestado hoy, o `null` si no ha contestado.
 *
 * Se guarda con la fecha dentro y no solo el valor: la pestaña puede quedarse abierta toda la
 * noche, y la respuesta de ayer no dice nada de hoy. Sin la fecha, el que contestó «poca» un
 * martes seguiría con las exigencias bajadas el miércoles sin haberlo pedido.
 */
export function lee(almacen, dia = hoy()) {
  try {
    const crudo = JSON.parse(almacen.getItem(CLAVE));
    if (!crudo || crudo.dia !== dia) return null;
    return POR_VALOR[crudo.valor] ? crudo.valor : null;
  } catch {
    return null;
  }
}

export function guarda(almacen, valor, dia = hoy()) {
  if (!POR_VALOR[valor]) return null;
  try {
    almacen.setItem(CLAVE, JSON.stringify({ dia, valor }));
  } catch {
    // Sin almacenamiento se sigue igual: vale para esta pantalla y no se recuerda. Peor es
    // no dejar contestar.
  }
  return valor;
}

/** Se le quita del medio: vuelve a estar como si no hubiera contestado. */
export function olvida(almacen) {
  try { almacen.removeItem(CLAVE); } catch { /* sin almacén */ }
}
